import { useParams, useSearch, useNavigate } from '@tanstack/react-router';
import {
  useGetWorkflowDefinition,
  useUpdateWorkflowDefinition,
  useCreateWorkflowDefinition,
} from 'src/api/rpc/hooks/workflows/useWorkflowDefinitions';
import { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  MiniMap,
  Panel,
  type NodeTypes,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { StepNode } from '@/lib/steps/step-node';
import { definitionToGraph } from 'src/lib/workflows/definitionToGraph';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from 'src/components/ui/card';
import { Badge } from 'src/components/ui/badge';
import { Button } from 'src/components/ui/button';
import { Switch } from 'src/components/ui/switch';
import {
  PencilIcon,
  CircleNotchIcon,
  XIcon,
  GearSixIcon,
} from '@phosphor-icons/react';
import { WorkflowEditor, type WorkflowMetadata } from './WorkflowEditor';
import type { WorkflowDefinitionResponse } from '@erp/rpc';
import { useToast } from '@/context/ToastContext';
import {
  findFloatingNodes,
  findUnconnectedRequiredSockets,
  detectCycles,
  generateSocketsFromSchema,
  SystemEventMap,
  type ValidationStep,
  type ValidationEdge,
} from '@erp/workflows';
import { SystemEvent } from '@erp/shared-types';
import paths from 'src/paths';
import { ScrollArea } from 'src/components/ui/scroll-area';
import { Separator } from 'src/components/ui/separator';
import {
  getConfigRenderer,
  type StepNode as StepNodeType,
} from './components/config-renderers';
import type { Node } from '@xyflow/react';
import { cn } from 'src/lib/utils';
import { useListWorkflowTemplates } from 'src/api/rpc/hooks/workflows/useWorkflowTemplates';
import { templateToEditorDefinition } from 'src/lib/workflows/templateToEditorDefinition';
import { useAnimatedWorkflow } from './hooks/useAnimatedWorkflow';

const nodeTypes: NodeTypes = {
  stepNode: StepNode,
};

interface ReadOnlyConfigPanelProps {
  node: StepNodeType;
  onClose: () => void;
}

function ReadOnlyConfigPanel({ node, onClose }: ReadOnlyConfigPanelProps) {
  const nodeData = node.data;
  const stepType = nodeData?.stepType ?? 'unknown';
  const stepCategory = nodeData?._stepTypeData?.category ?? 'Utility';

  const ConfigRenderer = useMemo(() => {
    if (!nodeData?.stepType) return null;
    return getConfigRenderer(nodeData.stepType);
  }, [nodeData?.stepType]);

  return (
    <div
      className={cn(
        'bg-background border rounded-lg shadow-lg flex flex-col overflow-hidden',
        'w-[22rem] max-h-[400px]',
      )}
    >
      <div className="flex items-center justify-between p-1.5 border-b flex-shrink-0">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <GearSixIcon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
          <span className="text-sm font-medium truncate">{nodeData.label}</span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 flex-shrink-0"
          onClick={onClose}
        >
          <XIcon className="h-4 w-4" />
        </Button>
      </div>

      <div className="p-3 border-b flex-shrink-0">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="outline" className="text-xs">
            {stepCategory}
          </Badge>
          <span className="text-xs text-muted-foreground font-mono">
            {stepType}
          </span>
        </div>
        {nodeData._stepTypeData?.description && (
          <p className="text-xs text-muted-foreground mt-2">
            {nodeData._stepTypeData.description}
          </p>
        )}
      </div>

      <Separator />

      <ScrollArea className="flex-1 min-h-0 p-3">
        {ConfigRenderer && (
          <div className="pointer-events-none opacity-80">
            <ConfigRenderer
              node={node}
              config={nodeData.config ?? {}}
              onConfigChange={() => {}}
            />
          </div>
        )}
      </ScrollArea>
    </div>
  );
}

function createEmptyWorkflowDefinition(options?: {
  emailRuleOptionId?: string;
}): WorkflowDefinitionResponse {
  const defaultEventType = SystemEvent.EMAIL_ROUTED_EMAIL_RULE;
  const eventSchema = SystemEventMap[defaultEventType];
  const outputSockets = generateSocketsFromSchema(eventSchema, 'output');

  const triggerConfig: Record<string, unknown> = {
    eventType: defaultEventType,
  };

  if (options?.emailRuleOptionId) {
    triggerConfig.emailRuleOptionId = options.emailRuleOptionId;
  }

  return {
    id: 'new',
    name: '',
    description: '',
    tags: [],
    version: 1,
    isActive: false,
    organizationId: '',
    createdBy: '',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    steps: [
      {
        id: crypto.randomUUID(),
        type: 'event_trigger',
        name: 'Event Trigger',
        category: 'Trigger',
        config: triggerConfig,
        sockets: {
          inputs: [],
          outputs: outputSockets,
        },
        position: { x: 250, y: 100 },
      },
    ],
    edges: [],
  };
}

export function WorkflowDefinitionOverviewTab() {
  const { workflowDefinitionId } = useParams({ strict: false });
  const { edit, emailRuleOptionId, aiCreationId } = useSearch({
    strict: false,
  }) as {
    edit?: boolean;
    emailRuleOptionId?: string;
    aiCreationId?: string;
  };
  const navigate = useNavigate();
  const { showToast } = useToast();
  const isEditMode = !!edit;
  const isCreateMode = workflowDefinitionId === 'new';
  const isTemplateCreateMode = isCreateMode && !!aiCreationId;

  const [stubDefinition, setStubDefinition] = useState(() =>
    createEmptyWorkflowDefinition({ emailRuleOptionId }),
  );

  const {
    data: templatesData,
    isLoading: isTemplatesLoading,
    error: templatesError,
  } = useListWorkflowTemplates();

  const lastResolvedTemplateIdRef = useRef<string | null>(null);
  const [isTemplateResolved, setIsTemplateResolved] = useState(
    () => !isTemplateCreateMode,
  );

  useEffect(() => {
    if (!isTemplateCreateMode) {
      setIsTemplateResolved(true);
      lastResolvedTemplateIdRef.current = null;
      return;
    }

    if (lastResolvedTemplateIdRef.current !== aiCreationId) {
      setIsTemplateResolved(false);
    }
  }, [isTemplateCreateMode, aiCreationId]);

  useEffect(() => {
    if (!isTemplateCreateMode) return;
    if (isTemplateResolved) return;

    // Still loading templates
    if (!templatesData && !templatesError) return;

    const fallbackToEmpty = () => {
      setStubDefinition(createEmptyWorkflowDefinition({ emailRuleOptionId }));
      setIsTemplateResolved(true);
      lastResolvedTemplateIdRef.current = aiCreationId ?? null;
    };

    if (templatesError) {
      showToast({
        title: 'Failed to load templates',
        message: 'Starting with an empty workflow.',
        color: 'error',
      });
      fallbackToEmpty();
      return;
    }

    const template = templatesData?.templates.find(
      (t) => t.id === aiCreationId,
    );
    if (!template) {
      showToast({
        title: 'Template not found',
        message: `Template "${aiCreationId}" not found. Starting with empty workflow.`,
        color: 'error',
      });
      fallbackToEmpty();
      return;
    }

    try {
      setStubDefinition(templateToEditorDefinition(template));
    } catch (error) {
      showToast({
        title: 'Failed to load template',
        message:
          error instanceof Error
            ? error.message
            : 'Starting with an empty workflow.',
        color: 'error',
      });
      setStubDefinition(createEmptyWorkflowDefinition({ emailRuleOptionId }));
    }

    setIsTemplateResolved(true);
    lastResolvedTemplateIdRef.current = aiCreationId ?? null;
  }, [
    aiCreationId,
    emailRuleOptionId,
    isTemplateCreateMode,
    isTemplateResolved,
    showToast,
    templatesData,
    templatesError,
  ]);

  // Skip fetching in create mode
  const {
    data: fetchedDefinition,
    isLoading,
    refetch,
  } = useGetWorkflowDefinition(isCreateMode ? undefined : workflowDefinitionId);

  const updateMutation = useUpdateWorkflowDefinition();
  const createMutation = useCreateWorkflowDefinition();

  // Use stub for create mode, fetched for edit mode
  const definition = isCreateMode ? stubDefinition : fetchedDefinition;

  const safeDefinitionForGraph = definition ?? stubDefinition;

  const animatedGraph = useAnimatedWorkflow(safeDefinitionForGraph);

  const staticGraph = useMemo(() => {
    if (!safeDefinitionForGraph) {
      return { nodes: [], edges: [] };
    }
    return definitionToGraph(safeDefinitionForGraph);
  }, [safeDefinitionForGraph]);

  const shouldUseAnimatedGraph = isTemplateCreateMode;
  const { nodes, edges } = shouldUseAnimatedGraph ? animatedGraph : staticGraph;
  const isAnimating = shouldUseAnimatedGraph
    ? animatedGraph.isAnimating
    : false;

  const WorkflowEditorWithAnimating =
    WorkflowEditor as unknown as React.ComponentType<
      React.ComponentProps<typeof WorkflowEditor> & { isAnimating?: boolean }
    >;

  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);

  const selectedNode = useMemo(() => {
    if (!selectedNodeId) return null;
    const found = nodes.find((n: Node) => n.id === selectedNodeId);
    if (!found) return null;
    if (found.type === 'stepNode' && 'label' in (found.data ?? {})) {
      return found as StepNodeType;
    }
    return null;
  }, [nodes, selectedNodeId]);

  const handleNodeClick = useCallback(
    (_event: React.MouseEvent, node: Node) => {
      setSelectedNodeId(node.id);
    },
    [],
  );

  const handlePaneClick = useCallback(() => {
    setSelectedNodeId(null);
  }, []);

  const handleCloseConfigPanel = useCallback(() => {
    setSelectedNodeId(null);
  }, []);

  const handleSave = useCallback(
    async (
      steps: WorkflowDefinitionResponse['steps'],
      edges: WorkflowDefinitionResponse['edges'],
      metadata?: WorkflowMetadata,
    ) => {
      if (!definition) return;

      try {
        if (isCreateMode) {
          // Create new workflow
          if (!metadata?.name?.trim()) {
            showToast({
              title: 'Name required',
              message: 'Please enter a name for the workflow.',
              color: 'error',
            });
            return;
          }

          const result = await createMutation.mutateAsync({
            name: metadata.name.trim(),
            description: metadata.description?.trim() || undefined,
            tags: metadata.tags?.filter((t) => t.trim()) || [],
            steps,
            edges,
          });

          showToast({
            title: 'Workflow created',
            message: 'Your workflow has been created successfully.',
            color: 'success',
          });

          // Navigate to the new workflow
          void navigate({
            to: paths.authenticated.workflows.definition,
            params: { workflowDefinitionId: result.id },
          });
        } else {
          // Update existing workflow
          await updateMutation.mutateAsync({
            workflowDefinitionId: definition.id,
            name: metadata?.name?.trim() || undefined,
            description: metadata?.description?.trim() || undefined,
            tags: metadata?.tags?.filter((t) => t.trim()),
            steps,
            edges,
          });
          await refetch();
          void navigate({
            to: '.',
            search: { edit: undefined },
          });
          showToast({
            title: 'Workflow saved',
            message: 'Your changes have been saved successfully.',
            color: 'success',
          });
        }
      } catch (error) {
        showToast({
          title: 'Failed to save',
          message:
            error instanceof Error
              ? error.message
              : 'An error occurred while saving.',
          color: 'error',
        });
      }
    },
    [
      definition,
      isCreateMode,
      createMutation,
      updateMutation,
      refetch,
      showToast,
      navigate,
    ],
  );

  const handleCancel = useCallback(() => {
    if (isCreateMode) {
      // Navigate back to workflow list
      void navigate({
        to: paths.authenticated.workflows.list,
      });
    } else {
      void navigate({
        to: '.',
        search: { edit: undefined },
      });
    }
  }, [isCreateMode, navigate]);

  // Handle toggling active/inactive status
  const handleToggleActive = useCallback(async () => {
    if (!definition || isCreateMode) return;

    const newActiveState = !definition.isActive;

    // If activating, validate the workflow first
    if (newActiveState) {
      // Convert definition to validation format
      const validationSteps: ValidationStep[] = definition.steps.map(
        (step: WorkflowDefinitionResponse['steps'][0]) => ({
          id: step.id,
          type: step.type,
          category: step.category,
          sockets: {
            inputs: step.sockets.inputs.map((s) => ({
              id: s.id,
              name: s.name,
              required: s.required ?? true,
            })),
            outputs: step.sockets.outputs.map((s) => ({
              id: s.id,
              name: s.name,
            })),
          },
        }),
      );
      const validationEdges: ValidationEdge[] = definition.edges.map(
        (edge: WorkflowDefinitionResponse['edges'][0]) => ({
          id: edge.id,
          sourceStepId: edge.sourceStepId,
          sourceSocketId: edge.sourceSocketId,
          targetStepId: edge.targetStepId,
          targetSocketId: edge.targetSocketId,
        }),
      );

      // Check for cycles
      const cycleResult = detectCycles(validationSteps, validationEdges);
      if (cycleResult.hasCycle) {
        showToast({
          title: 'Cannot activate',
          message:
            'Workflow contains a cycle. Fix the cycle before activating.',
          color: 'error',
        });
        return;
      }

      // Check for floating nodes
      const floatingNodes = findFloatingNodes(validationSteps, validationEdges);
      if (floatingNodes.length > 0) {
        showToast({
          title: 'Cannot activate',
          message: `${floatingNodes.length} node${floatingNodes.length > 1 ? 's are' : ' is'} not connected. Connect all nodes before activating.`,
          color: 'error',
        });
        return;
      }

      // Check for unconnected required sockets
      const unconnectedSockets = findUnconnectedRequiredSockets(
        validationSteps,
        validationEdges,
      );
      if (unconnectedSockets.length > 0) {
        showToast({
          title: 'Cannot activate',
          message: `${unconnectedSockets.length} required input${unconnectedSockets.length > 1 ? 's are' : ' is'} not connected. Connect all required inputs before activating.`,
          color: 'error',
        });
        return;
      }
    }

    try {
      await updateMutation.mutateAsync({
        workflowDefinitionId: definition.id,
        isActive: newActiveState,
      });
      await refetch();
      showToast({
        title: newActiveState ? 'Workflow activated' : 'Workflow deactivated',
        message: newActiveState
          ? 'This workflow will now respond to triggers.'
          : 'This workflow will no longer respond to triggers.',
        color: 'success',
      });
    } catch (error) {
      showToast({
        title: 'Failed to update status',
        message:
          error instanceof Error
            ? error.message
            : 'An error occurred while updating the workflow status.',
        color: 'error',
      });
    }
  }, [definition, isCreateMode, updateMutation, refetch, showToast]);

  if (isTemplateCreateMode && !isTemplateResolved) {
    return (
      <div className="h-[calc(100vh-120px)] min-h-[700px] -mx-6 -mb-6 flex flex-col items-center justify-center gap-3">
        <CircleNotchIcon className="w-6 h-6 animate-spin text-muted-foreground" />
        <div className="text-sm text-muted-foreground">
          {isTemplatesLoading ? 'Loading template...' : 'Preparing template...'}
        </div>
      </div>
    );
  }

  // Loading state only applies to edit mode (not create)
  if (!isCreateMode && (isLoading || !definition)) {
    return null; // Layout handles loading state
  }

  // Edit Mode - Full screen editor (also used for create mode)
  if (isEditMode && definition) {
    return (
      <div className="h-[calc(100vh-120px)] min-h-[700px] -mx-6 -mb-6 flex flex-col">
        <WorkflowEditorWithAnimating
          definition={definition}
          onSave={handleSave}
          onCancel={handleCancel}
          isSaving={
            (isCreateMode
              ? createMutation.isPending
              : updateMutation.isPending) || isAnimating
          }
          isCreateMode={isCreateMode}
          initiallyCollapsed={isTemplateCreateMode}
          isAnimating={isAnimating}
        />
      </div>
    );
  }

  // View Mode - Read-only overview (only for existing workflows)
  if (!definition) {
    return null;
  }

  return (
    <div className="space-y-4">
      {/* Summary Info with Edit Button */}
      <div className="flex items-center justify-between">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 flex-1">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Steps</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {definition.steps.length}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Connections</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {definition.edges.length}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3">
                <Switch
                  checked={definition.isActive}
                  onCheckedChange={() => void handleToggleActive()}
                  disabled={updateMutation.isPending}
                />
                <div className="flex items-center gap-2">
                  {updateMutation.isPending ? (
                    <CircleNotchIcon className="w-4 h-4 animate-spin text-muted-foreground" />
                  ) : null}
                  <Badge
                    variant={definition.isActive ? 'default' : 'secondary'}
                  >
                    {definition.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Graph Visualization */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle>Workflow Graph</CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void navigate({ to: '.', search: { edit: true } })}
          >
            <PencilIcon className="w-4 h-4 mr-1" />
            Edit Workflow
          </Button>
        </CardHeader>
        <CardContent>
          <div
            style={{ height: '500px', width: '100%' }}
            className="rounded-lg border"
          >
            <ReactFlowProvider>
              <ReactFlow
                nodes={nodes}
                edges={edges}
                nodeTypes={nodeTypes}
                fitView
                fitViewOptions={{ maxZoom: 0.75, padding: 0.4 }}
                attributionPosition="bottom-left"
                proOptions={{ hideAttribution: true }}
                minZoom={0.2}
                maxZoom={1}
                defaultEdgeOptions={{
                  animated: true,
                }}
                nodesDraggable={false}
                nodesConnectable={false}
                elementsSelectable={false}
                panOnDrag={true}
                zoomOnScroll={true}
                onNodeClick={handleNodeClick}
                onPaneClick={handlePaneClick}
              >
                <Background />
                <Controls />
                <MiniMap />
                {selectedNode && (
                  <Panel position="top-right" className="m-2">
                    <ReadOnlyConfigPanel
                      node={selectedNode}
                      onClose={handleCloseConfigPanel}
                    />
                  </Panel>
                )}
              </ReactFlow>
            </ReactFlowProvider>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
